import { motion } from "framer-motion";
import { Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFileAlt, faPaperPlane, faEye } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectSubscriptionCurrency } from "redux-context/business";
import { PendingActivity } from "./PendingActivities";
import ActivityItem from "./ActivityItem";
import EmptyState from "./EmptyState";
import RescheduleRequest from "components/reschedule-request/RescheduleRequest";
import { computeTimeLeft, canRescheduleAccepted } from "./PendingActivities";
import { formatExperienceCredit } from "helpers/experience-credit-format";
import "./PendingActivities.scss";

const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

interface ApplicationsTabProps {
  applications: PendingActivity[];
  loading: boolean;
  showCreatorInfo?: boolean;
  onAccept?: (evt: any, influencer: PendingActivity) => void;
  onReject?: (evt: any, influencer: PendingActivity) => void;
  onReschedule?: (activity: PendingActivity) => void;
}

const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  applications,
  loading,
  showCreatorInfo,
  onAccept,
  onReject,
}) => {
  const subscriptionCurrency = useSelector(selectSubscriptionCurrency);

  if (applications.length === 0 && !loading) {
    return (
      <EmptyState
        icon={faFileAlt}
        title="No Applications Received"
        description="This creator hasn't applied to any of your campaigns yet. You can invite them directly to specific campaigns or wait for them to discover and apply to your open collaborations."
        actions={
          <>
            <Button
              type="primary"
              onClick={() => {
                /* open send campaign invite flow */
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
              Send Campaign Invite
            </Button>
            <Button
              onClick={() => {
                /* open view campaigns */
              }}
            >
              <FontAwesomeIcon icon={faEye} style={{ marginRight: 8 }} />
              View Open Campaigns
            </Button>
          </>
        }
      />
    );
  }

  return (
    <motion.div
      className="activity-list bookings-list"
      variants={listVariants}
      initial="hidden"
      animate="visible"
    >
      {applications.map((application) => (
        <ActivityItem
          key={application.id}
          activity={application}
          showCreatorInfo={showCreatorInfo}
          type="application"
          renderCompensation={() =>
            (application.foodCompAmount || application.cashCompAmount) ? (
              <div className="booking-comp">
                {application.foodCompAmount
                  ? formatExperienceCredit(
                      application.foodCompAmount,
                      subscriptionCurrency ?? undefined,
                    )
                  : null}
                {application.foodCompAmount && application.cashCompAmount
                  ? " · "
                  : null}
                {application.cashCompAmount
                  ? `Cash Comp: $${application.cashCompAmount}`
                  : null}
              </div>
            ) : null
          }
          renderDateTime={() => (
            <div className="booking-datetime">
              <div className="booking-date">
                Applied:{" "}
                <span>
                  {dayjs(application?.requestedAt).format("MMMM D, YYYY")}
                </span>
              </div>
              <div className="application-left-pill">
                {application.dueDateToReview
                  ? computeTimeLeft(application.dueDateToReview)
                  : (application as any).timeLeft || ""}
              </div>
            </div>
          )}
          renderActions={() => (
            <>
              {canRescheduleAccepted(application) &&
                application.bookingId &&
                application.collabId && (
                  <RescheduleRequest
                    asButton
                    influencer={{
                      bookingId: application.bookingId,
                      collabId: application.collabId,
                      tabStatus: (application.tabStatus?.toLowerCase() ||
                        "pending") as "accepted" | "pending" | "rejected" | "expired",
                      isPremium: application.isPremium,
                      selfManagedCompensationEnabled:
                        application.selfManagedCompensationEnabled,
                      cashCompAmount: application.cashCompAmount ?? undefined,
                    }}
                  />
                )}
              <motion.div
                style={{ display: "inline-block", marginRight: 8 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  type="primary"
                  onClick={(e) => onAccept?.(e, application)}
                >
                  <FontAwesomeIcon icon={faCheck} /> Accept
                </Button>
              </motion.div>
              <motion.div
                style={{ display: "inline-block" }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  className="red-hover"
                  onClick={(e) => onReject?.(e, application)}
                >
                  Decline
                </Button>
              </motion.div>
            </>
          )}
        />
      ))}
    </motion.div>
  );
};

export default ApplicationsTab;
